// src/components/CrewBoardView.tsx
import React from "react";
import { createPortal } from "react-dom";
import type { ToastType, UserProfile } from "../types";
import { resolveRoddoDarkMode } from "../constants/theme";
import { ShareIcon } from "./icons";
import {
  addCrewPostComment,
  createCrewPost,
  deleteCrewPost,
  deleteCrewPostComment,
  getCrewPostById,
  subscribeCrewPostComments,
  subscribeCrewPostCommentLikeByMe,
  subscribeCrewPostLikeByMe,
  subscribeCrewPosts,
  toggleCrewPostCommentLike,
  toggleCrewPostLike,
  updateCrewPost,
  updateCrewPostComment,
  type CrewPost,
  type CrewPostComment,
} from "../services/crews";
import { buildCrewPostShareUrl, share } from "../utils/share";

type Props = {
  crewId: string;
  crewTitle?: string;
  memberNames?: string[];
  currentUser: UserProfile;
  isDarkMode?: boolean;
  addToast: (message: string, type?: ToastType) => void;
  onOpenProfile?: (uid: string) => void;
  canModerate?: boolean;
  initialPostId?: string;
};

const clean = (v: any) => String(v ?? "").trim();
const DEFAULT_AVATAR = "/avatar-default.png";

type CrewReactionKey = "like" | "love" | "cry" | "laugh" | "angry" | "wow";

const CREW_REACTIONS: Array<{
  key: CrewReactionKey;
  label: string;
  emoji: string;
}> = [
  { key: "like", label: "Like", emoji: "👍" },
  {
    key: "love",
    label: "Love",
    emoji: "❤️",
  },
  {
    key: "laugh",
    label: "Laugh",
    emoji: "😂",
  },
  {
    key: "cry",
    label: "Cry",
    emoji: "😢",
  },
  {
    key: "angry",
    label: "Mad",
    emoji: "😡",
  },
  { key: "wow", label: "Wow", emoji: "😮" },
];

const DEFAULT_CREW_REACTION = CREW_REACTIONS[0];

const normalizeCrewReactionKey = (value: any): CrewReactionKey | null => {
  const key = clean(value).toLowerCase();
  return CREW_REACTIONS.some((reaction) => reaction.key === key)
    ? (key as CrewReactionKey)
    : null;
};

const getCrewReactionByKey = (value: any) => {
  const key = normalizeCrewReactionKey(value);
  return (
    CREW_REACTIONS.find((reaction) => reaction.key === key) ||
    DEFAULT_CREW_REACTION
  );
};

const displayNameFor = (user: any) =>
  clean(user?.name) ||
  clean(user?.displayName) ||
  clean(user?.username) ||
  clean(user?.handle) ||
  "Angler";

const avatarFor = (user: any) =>
  clean(user?.avatar) ||
  clean(user?.avatarUrl) ||
  clean(user?.photoURL) ||
  DEFAULT_AVATAR;

const fmtTime = (ms?: number) => {
  if (!ms) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(ms));
  } catch {
    return "";
  }
};

const editedLabel = (post: CrewPost) => {
  const editedAt = Number((post as any)?.editedAt || 0);
  const createdAt = Number(post?.createdAt || post?.timestamp || 0);
  return editedAt && editedAt > createdAt + 1000 ? " · edited" : "";
};

type Styles = ReturnType<typeof buildStyles>;

function buildStyles(isDarkMode: boolean) {
  const pageText = isDarkMode ? "#F2F6FC" : "#30271E";
  const softText = isDarkMode ? "#A8B4C7" : "#6E6255";
  return {
    pageText,
    softText,
    boardBg: isDarkMode ? "#070B15" : "#F7F2E8",
    postBg: isDarkMode ? "#101827" : "#FFFCF7",
    rowBg: isDarkMode ? "#0F1828" : "#F7F2E8",
    commentBg: isDarkMode ? "#0A1120" : "#F2EBE1",
    inputBg: isDarkMode ? "#0C1423" : "#FFFCF7",
    border: isDarkMode ? "rgba(132,151,190,0.14)" : "rgba(74,58,42,0.14)",
    borderStrong: isDarkMode ? "rgba(132,151,190,0.20)" : "rgba(74,58,42,0.22)",
    primaryButtonBg: isDarkMode ? "#8A70F2" : "#2F80ED",
    primaryButtonText: "#FFFFFF",
    primaryBorder: isDarkMode ? "rgba(112,87,232,0.70)" : "rgba(47,128,237,0.62)",
    secondaryBg: isDarkMode ? "#0F1828" : "#F2EBE1",
    likedBg: isDarkMode ? "rgba(138,112,242,0.10)" : "rgba(47,128,237,0.09)",
    likedText: isDarkMode ? "#D9D2FF" : "#1F68B8",
    likedBorder: isDarkMode ? "rgba(138,112,242,0.24)" : "rgba(47,128,237,0.24)",
    dangerText: isDarkMode ? "#FF9AA5" : "#A63D49",
    menuBg: isDarkMode ? "#0F1828" : "#FFFCF7",
    shadow: isDarkMode ? "0 6px 16px rgba(0,0,0,0.16)" : "0 10px 24px rgba(62,45,28,0.10)",
    cardShadow: isDarkMode ? "0 4px 12px rgba(0,0,0,0.12)" : "0 4px 14px rgba(62,45,28,0.06)",
  };
}

const CrewPostMenu: React.FC<{
  styles: Styles;
  onShare?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
}> = ({ styles, onShare, onEdit, onRemove, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });

  const hasAny = Boolean(onShare) || Boolean(onEdit) || Boolean(onRemove);

  const measure = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: Math.round(r.bottom + 8), left: Math.round(r.right) });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    measure();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => measure();

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, measure]);

  if (!hasAny) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xl font-black leading-none transition hover:-translate-y-0.5 disabled:opacity-50"
        style={{ color: styles.pageText, background: styles.secondaryBg }}
        aria-label="More post options"
        title="More"
      >
        ⋯
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[99999] w-48 overflow-hidden rounded-[10px] border backdrop-blur-2xl"
            style={{
              top: pos.top,
              left: pos.left,
              transform: "translateX(-100%)",
              background: styles.menuBg,
              borderColor: styles.border,
              boxShadow: styles.shadow,
            }}
            role="menu"
          >
            {onShare ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onShare();
                }}
                className="w-full px-4 py-3 text-left text-sm font-black hover:bg-black/5 dark:hover:bg-white/10"
                style={{ color: styles.pageText }}
                role="menuitem"
              >
                <span className="inline-flex items-center gap-2">
                  <ShareIcon className="h-[16px] w-[16px]" />
                  <span>Share</span>
                </span>
              </button>
            ) : null}

            {onEdit ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onEdit();
                }}
                className="w-full px-4 py-3 text-left text-sm font-black hover:bg-black/5 dark:hover:bg-white/10"
                style={{ color: styles.pageText }}
                role="menuitem"
              >
                ✏️ Edit
              </button>
            ) : null}

            {onRemove ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onRemove();
                }}
                className="w-full px-4 py-3 text-left text-sm font-black hover:bg-black/5 dark:hover:bg-white/10"
                style={{ color: styles.dangerText }}
                role="menuitem"
              >
                🗑️ Remove
              </button>
            ) : null}
          </div>,
          document.body,
        )}
    </>
  );
};

const CrewReactionButton: React.FC<{
  styles: Styles;
  count: number;
  reaction: CrewReactionKey | null;
  disabled?: boolean;
  onReact: (reaction: CrewReactionKey) => void;
}> = ({ styles, count, reaction, disabled, onReact }) => {
  const [trayOpen, setTrayOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const holdTimerRef = React.useRef<number | null>(null);
  const longPressFiredRef = React.useRef(false);

  const selectedReaction = reaction ? getCrewReactionByKey(reaction) : null;

  const clearHoldTimer = React.useCallback(() => {
    if (holdTimerRef.current) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const openTray = React.useCallback(() => {
    if (disabled) return;
    clearHoldTimer();
    setTrayOpen(true);
  }, [clearHoldTimer, disabled]);

  const startHold = React.useCallback(() => {
    if (disabled) return;
    clearHoldTimer();
    longPressFiredRef.current = false;
    holdTimerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      setTrayOpen(true);
    }, 420);
  }, [clearHoldTimer, disabled]);

  const handleMainClick = React.useCallback(() => {
    if (disabled) return;
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    onReact(reaction || DEFAULT_CREW_REACTION.key);
  }, [disabled, onReact, reaction]);

  const chooseReaction = React.useCallback(
    (next: CrewReactionKey) => {
      setTrayOpen(false);
      onReact(next);
    },
    [onReact],
  );

  React.useEffect(() => {
    return () => clearHoldTimer();
  }, [clearHoldTimer]);

  React.useEffect(() => {
    if (!trayOpen) return;

    const onDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && wrapRef.current?.contains(target)) return;
      setTrayOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTrayOpen(false);
    };

    window.addEventListener("mousedown", onDown);
    window.addEventListener("touchstart", onDown, { passive: true });
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("touchstart", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [trayOpen]);

  return (
    <div
      ref={wrapRef}
      className="relative inline-flex items-center"
      aria-label="Crew post reaction"
    >
      {trayOpen ? (
        <div
          className="absolute bottom-[calc(100%+10px)] left-0 z-30 flex items-center gap-1.5 rounded-[14px] border px-2 py-1.5"
          style={{ background: styles.menuBg, borderColor: styles.borderStrong, boxShadow: styles.shadow }}
          onMouseDown={(e) => e.preventDefault()}
          role="menu"
          aria-label="Choose reaction"
        >
          {CREW_REACTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => chooseReaction(item.key)}
              className="grid h-10 w-10 place-items-center rounded-[10px] border text-xl transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2"
              style={{ background: styles.secondaryBg, borderColor: styles.border, color: styles.pageText }}
              title={item.label}
              aria-label={item.label}
              aria-pressed={reaction === item.key}
              role="menuitem"
            >
              <span aria-hidden="true">{item.emoji}</span>
            </button>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        disabled={disabled}
        onClick={handleMainClick}
        onDoubleClick={openTray}
        onMouseDown={startHold}
        onMouseUp={clearHoldTimer}
        onMouseLeave={clearHoldTimer}
        onTouchStart={startHold}
        onTouchEnd={clearHoldTimer}
        onContextMenu={(event) => {
          event.preventDefault();
          openTray();
        }}
        className="inline-flex min-w-[48px] items-center justify-center gap-1 rounded-[10px] border px-3 py-2 text-xs font-black transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
        style={{
          background: selectedReaction ? styles.likedBg : styles.secondaryBg,
          borderColor: selectedReaction ? styles.likedBorder : styles.border,
          color: selectedReaction ? styles.likedText : styles.pageText,
        }}
        aria-pressed={Boolean(selectedReaction)}
        title="Tap to react. Press and hold for more reactions."
      >
        <span className="text-base leading-none" aria-hidden="true">
          {selectedReaction ? selectedReaction.emoji : "♡"}
        </span>
        {count > 0 ? (
          <span className="text-xs font-black leading-none">{count}</span>
        ) : null}
      </button>
    </div>
  );
};

const CrewComments: React.FC<{
  crewId: string;
  postId: string;
  currentUser: UserProfile;
  addToast: Props["addToast"];
  styles: Styles;
  onOpenProfile?: (uid: string) => void;
  canModerate?: boolean;
}> = ({ crewId, postId, currentUser, addToast, styles, onOpenProfile, canModerate = false }) => {
  const [comments, setComments] = React.useState<CrewPostComment[]>([]);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submitLockRef = React.useRef(false);
  const [editingCommentId, setEditingCommentId] = React.useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = React.useState("");
  const [savingCommentEdit, setSavingCommentEdit] = React.useState(false);
  const [openCommentMenuId, setOpenCommentMenuId] = React.useState<string | null>(null);
  const [deletingCommentId, setDeletingCommentId] = React.useState<string | null>(null);
  const [commentReactions, setCommentReactions] = React.useState<Record<string, CrewReactionKey>>({});
  const [pendingCommentReactionIds, setPendingCommentReactionIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    return subscribeCrewPostComments(crewId, postId, setComments, () =>
      addToast("Could not load crew comments.", "error"),
    );
  }, [crewId, postId, addToast]);

  React.useEffect(() => {
    const uid = clean((currentUser as any)?.uid);
    setCommentReactions({});
    if (!crewId || !postId || !uid || comments.length === 0) return;

    const unsubscribers = comments.map((comment) =>
      subscribeCrewPostCommentLikeByMe(
        crewId,
        postId,
        comment.id,
        uid,
        (reaction) => {
          setCommentReactions((prev) => {
            const next = { ...prev };
            const normalized = normalizeCrewReactionKey(reaction);
            if (normalized) next[comment.id] = normalized;
            else delete next[comment.id];
            return next;
          });
        },
        () => undefined,
      ),
    );

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [crewId, postId, currentUser, comments]);

  const reactToComment = async (commentId: string, reaction: CrewReactionKey = "like") => {
    const uid = clean((currentUser as any)?.uid);
    const id = clean(commentId);
    if (!uid || !id || pendingCommentReactionIds.has(id)) return;

    setPendingCommentReactionIds((prev) => new Set(prev).add(id));
    try {
      await toggleCrewPostCommentLike(crewId, postId, id, uid, reaction);
    } catch (err) {
      console.error("[CrewBoardView] comment reaction failed", err);
      addToast("Could not update crew reply reaction.", "error");
    } finally {
      setPendingCommentReactionIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const submit = async () => {
    const body = clean(text);
    if (!body || busy || submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      setBusy(true);
      await addCrewPostComment(crewId, postId, currentUser, body);
      setText("");
    } catch (err) {
      console.error("[CrewBoardView] add comment failed", err);
      addToast("Could not add crew comment.", "error");
    } finally {
      submitLockRef.current = false;
      setBusy(false);
    }
  };

  const startCommentEdit = (comment: CrewPostComment) => {
    setOpenCommentMenuId(null);
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text || "");
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const saveCommentEdit = async (commentId: string) => {
    const body = clean(editingCommentText);
    if (!commentId || !body || savingCommentEdit) return;
    try {
      setSavingCommentEdit(true);
      await updateCrewPostComment(crewId, postId, commentId, body);
      cancelCommentEdit();
      addToast("Crew reply updated.", "success");
    } catch (err) {
      console.error("[CrewBoardView] update comment failed", err);
      addToast("Could not update crew reply.", "error");
    } finally {
      setSavingCommentEdit(false);
    }
  };


  const deleteComment = async (commentId: string) => {
    const id = clean(commentId);
    if (!id || deletingCommentId) return;

    const ok = window.confirm("Remove this crew reply?");
    if (!ok) return;

    try {
      setDeletingCommentId(id);
      setOpenCommentMenuId(null);
      await deleteCrewPostComment(crewId, postId, id);
      if (editingCommentId === id) cancelCommentEdit();
      addToast("Crew reply removed.", "success");
    } catch (err) {
      console.error("[CrewBoardView] delete comment failed", err);
      addToast("Could not remove crew reply.", "error");
    } finally {
      setDeletingCommentId(null);
    }
  };

  return (
    <div
      className="mt-3 space-y-2 rounded-[18px] border p-3"
      style={{ background: styles.commentBg, borderColor: styles.border }}
    >
      {comments.length ? (
        comments.map((comment) => (
          <div
            key={comment.id}
            className="flex gap-2 rounded-[14px] border p-2.5"
            style={{ background: styles.postBg, borderColor: styles.border }}
          >
            <button
              type="button"
              onClick={() => onOpenProfile?.(comment.authorId)}
              className="h-8 w-8 shrink-0 overflow-hidden rounded-full border"
              style={{ borderColor: styles.border }}
            >
              <img
                src={comment.authorAvatar || DEFAULT_AVATAR}
                alt=""
                className="h-full w-full object-cover"
              />
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <button
                  type="button"
                  onClick={() => onOpenProfile?.(comment.authorId)}
                  className="truncate text-left text-xs font-black"
                  style={{ color: styles.pageText }}
                >
                  {comment.authorName || "Angler"}
                </button>
                <span
                  className="shrink-0 text-[10px] font-bold"
                  style={{ color: styles.softText }}
                >
                  {fmtTime(comment.timestamp)}
                </span>
              </div>
              {editingCommentId === comment.id ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={editingCommentText}
                    onChange={(e) => setEditingCommentText(e.target.value)}
                    rows={3}
                    className="w-full resize-none rounded-[10px] border px-3 py-2 text-xs font-semibold leading-5 outline-none"
                    style={{
                      background: styles.inputBg,
                      color: styles.pageText,
                      borderColor: styles.border,
                    }}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelCommentEdit}
                      disabled={savingCommentEdit}
                      className="rounded-xl border px-3 py-1.5 text-[11px] font-black disabled:opacity-60"
                      style={{
                        background: styles.secondaryBg,
                        borderColor: styles.border,
                        color: styles.pageText,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => saveCommentEdit(comment.id)}
                      disabled={savingCommentEdit || !clean(editingCommentText)}
                      className="rounded-xl border px-3 py-1.5 text-[11px] font-black disabled:opacity-60"
                      style={{
                        background: styles.primaryButtonBg,
                        borderColor: styles.primaryBorder,
                        color: styles.primaryButtonText,
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className="mt-0.5 whitespace-pre-wrap break-words text-xs font-semibold leading-5"
                  style={{ color: styles.pageText }}
                >
                  {comment.text}
                  {comment.editedAt ? (
                    <span className="ml-1 text-[10px] font-bold" style={{ color: styles.softText }}>
                      edited
                    </span>
                  ) : null}
                </p>
              )}

              {editingCommentId !== comment.id ? (
                <div className="mt-2 flex items-center gap-2">
                  <CrewReactionButton
                    styles={styles}
                    count={Math.max(0, Number((comment as any)?.likeCount || 0))}
                    reaction={commentReactions[comment.id] || null}
                    disabled={pendingCommentReactionIds.has(comment.id)}
                    onReact={(reaction) => reactToComment(comment.id, reaction)}
                  />
                </div>
              ) : null}
            </div>
            {(comment.authorId === currentUser.uid || canModerate) && editingCommentId !== comment.id ? (
              <div className="relative shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    setOpenCommentMenuId((prev) => (prev === comment.id ? null : comment.id))
                  }
                  className="h-7 w-7 rounded-full border text-sm font-black"
                  style={{
                    background: styles.secondaryBg,
                    borderColor: styles.border,
                    color: styles.pageText,
                  }}
                  aria-label="Reply options"
                  title="Reply options"
                >
                  ⋯
                </button>

                {openCommentMenuId === comment.id ? (
                  <div
                    className="absolute right-0 top-8 z-30 w-32 overflow-hidden rounded-[10px] border p-1 shadow-xl"
                    style={{ background: styles.menuBg, borderColor: styles.border }}
                  >
                    {comment.authorId === currentUser.uid ? (
                    <button
                      type="button"
                      onClick={() => startCommentEdit(comment)}
                      className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs font-black"
                      style={{ color: styles.pageText }}
                    >
                      <span aria-hidden="true">✏️</span>
                      <span>Edit</span>
                    </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => deleteComment(comment.id)}
                      disabled={deletingCommentId === comment.id}
                      className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-xs font-black disabled:opacity-60"
                      style={{ color: styles.dangerText }}
                    >
                      <span aria-hidden="true">🗑️</span>
                      <span>{deletingCommentId === comment.id ? "Removing" : "Delete"}</span>
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ))
      ) : (
        <div
          className="rounded-[10px] border border-dashed p-3 text-xs font-bold"
          style={{ borderColor: styles.border, color: styles.softText }}
        >
          No replies yet. Drop the first crew reply.
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Reply to crew..."
          className="min-w-0 flex-1 rounded-[14px] border px-4 py-2 text-sm font-bold outline-none"
          style={{
            background: styles.inputBg,
            color: styles.pageText,
            borderColor: styles.border,
          }}
        />
        <button
          type="button"
          disabled={busy || !clean(text)}
          onClick={submit}
          className="rounded-[14px] border px-4 py-2 text-sm font-black disabled:opacity-50"
          style={{
            background: styles.primaryButtonBg,
            color: styles.primaryButtonText,
            borderColor: styles.primaryBorder,
          }}
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
};

const CrewBoardView: React.FC<Props> = ({
  crewId,
  crewTitle = "Trusted Crew Board",
  memberNames = [],
  currentUser,
  isDarkMode: explicitIsDarkMode,
  addToast,
  onOpenProfile,
  canModerate = false,
  initialPostId = "",
}) => {
  const uid = clean((currentUser as any)?.uid);
  const [posts, setPosts] = React.useState<CrewPost[]>([]);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const postSubmitLockRef = React.useRef(false);
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [openCommentsFor, setOpenCommentsFor] = React.useState<string | null>(
    null,
  );
  const [postReactions, setPostReactions] = React.useState<
    Record<string, CrewReactionKey>
  >({});
  const [pendingLikeIds, setPendingLikeIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [editingPostId, setEditingPostId] = React.useState<string | null>(null);
  const [editingText, setEditingText] = React.useState("");
  const [savingEdit, setSavingEdit] = React.useState(false);
  const [targetedPost, setTargetedPost] = React.useState<CrewPost | null>(null);

  const isDarkMode = resolveRoddoDarkMode(explicitIsDarkMode);
  const styles = React.useMemo(() => buildStyles(isDarkMode), [isDarkMode]);

  React.useEffect(() => {
    if (!crewId) {
      setPosts([]);
      return;
    }
    return subscribeCrewPosts(crewId, setPosts, () =>
      addToast("Could not load crew posts.", "error"),
    );
  }, [crewId, addToast]);

  React.useEffect(() => {
    const targetId = clean(initialPostId);
    if (!crewId || !targetId) {
      setTargetedPost(null);
      return;
    }

    const existing = posts.find((post) => post.id === targetId) || null;
    if (existing) {
      setTargetedPost(existing);
      return;
    }

    let cancelled = false;
    void getCrewPostById(crewId, targetId)
      .then((post) => {
        if (!cancelled) setTargetedPost(post);
      })
      .catch((err) => {
        console.warn("[CrewBoardView] shared crew post could not be opened", err);
        if (!cancelled) {
          setTargetedPost(null);
          addToast("That crew post is unavailable or private to another crew.", "info");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [crewId, initialPostId, posts, addToast]);

  const displayPosts = React.useMemo(() => {
    if (!targetedPost || posts.some((post) => post.id === targetedPost.id)) return posts;
    return [targetedPost, ...posts];
  }, [posts, targetedPost]);

  React.useEffect(() => {
    const targetId = clean(initialPostId);
    if (!targetId || !displayPosts.some((post) => post.id === targetId)) return;

    setOpenCommentsFor(targetId);
    const timer = window.setTimeout(() => {
      const nodes = Array.from(document.querySelectorAll<HTMLElement>("[data-crew-post-id]"));
      const target = nodes.find((node) => node.dataset.crewPostId === targetId);
      target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [displayPosts, initialPostId]);

  React.useEffect(() => {
    setPostReactions({});
    if (!crewId || !uid || posts.length === 0) return;

    const unsubscribers = posts.map((post) =>
      subscribeCrewPostLikeByMe(
        crewId,
        post.id,
        uid,
        (reaction) => {
          setPostReactions((prev) => {
            const next = { ...prev };
            const normalized = normalizeCrewReactionKey(reaction);
            if (normalized) next[post.id] = normalized;
            else delete next[post.id];
            return next;
          });
        },
        () => undefined,
      ),
    );

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [crewId, uid, posts]);

  const submitPost = async () => {
    const body = clean(text);
    if (!body || busy || postSubmitLockRef.current) return;
    postSubmitLockRef.current = true;
    try {
      setBusy(true);
      await createCrewPost(crewId, currentUser, body);
      setText("");
      setComposerOpen(false);
      addToast("Posted to crew.", "success");
    } catch (err) {
      console.error("[CrewBoardView] create post failed", err);
      addToast("Could not post to crew.", "error");
    } finally {
      postSubmitLockRef.current = false;
      setBusy(false);
    }
  };

  const reactToPost = async (
    postId: string,
    reaction: CrewReactionKey = "like",
  ) => {
    if (!uid || pendingLikeIds.has(postId)) return;
    setPendingLikeIds((prev) => new Set(prev).add(postId));
    try {
      await toggleCrewPostLike(crewId, postId, uid, reaction);
    } catch (err) {
      console.error("[CrewBoardView] reaction failed", err);
      addToast("Could not update crew reaction.", "error");
    } finally {
      setPendingLikeIds((prev) => {
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });
    }
  };

  const startEdit = (post: CrewPost) => {
    setEditingPostId(post.id);
    setEditingText(post.text || "");
    setOpenCommentsFor(null);
  };

  const cancelEdit = () => {
    setEditingPostId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    const postId = clean(editingPostId);
    const body = clean(editingText);
    if (!postId || !body || savingEdit) return;

    try {
      setSavingEdit(true);
      await updateCrewPost(crewId, postId, body);
      addToast("Crew post updated.", "success");
      cancelEdit();
    } catch (err) {
      console.error("[CrewBoardView] edit failed", err);
      addToast("Could not edit crew post.", "error");
    } finally {
      setSavingEdit(false);
    }
  };

  const hidePost = async (postId: string) => {
    try {
      await deleteCrewPost(crewId, postId);
      addToast("Crew post removed.", "success");
    } catch (err) {
      console.error("[CrewBoardView] delete failed", err);
      addToast("Could not remove crew post.", "error");
    }
  };

  const composerHasText = Boolean(clean(text));
  const boardLabel = clean(crewTitle) || "Trusted Crew Board";
  const composerPlaceholder = `Plan a trip, share a bite note, or drop a private crew update...`;

  return (
    <div className="space-y-3 pb-5">
      <section
        className="rounded-[18px] border p-3"
        style={{
          background: styles.postBg,
          borderColor: styles.border,
          color: styles.pageText,
          boxShadow: styles.cardShadow,
        }}
      >
        {!composerOpen ? (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="flex w-full items-center gap-3 rounded-[14px] border px-3 py-2.5 text-left transition hover:-translate-y-0.5 active:translate-y-0"
            style={{ background: styles.inputBg, borderColor: styles.border }}
          >
            <img
              src={avatarFor(currentUser)}
              alt=""
              className="h-10 w-10 shrink-0 rounded-full border object-cover"
              style={{ borderColor: styles.primaryBorder }}
            />
            <div className="min-w-0 flex-1">
              <div
                className="truncate text-sm font-black"
                style={{ color: styles.pageText }}
              >
                Start a crew update
              </div>
              <div
                className="mt-0.5 truncate text-xs font-bold"
                style={{ color: styles.softText }}
              >
                {composerHasText ? text.trim() : composerPlaceholder}
              </div>
            </div>
            <span
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-2xl font-black "
              style={{
                background: styles.primaryButtonBg,
                color: styles.primaryButtonText,
                border: `1px solid ${styles.primaryBorder}`,
              }}
              aria-hidden="true"
            >
              +
            </span>
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <img
                  src={avatarFor(currentUser)}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-full border object-cover"
                  style={{ borderColor: styles.primaryBorder }}
                />
                <div className="min-w-0">
                  <div
                    className="truncate text-sm font-black"
                    style={{ color: styles.pageText }}
                  >
                    Crew update
                  </div>
                  <div
                    className="truncate text-[11px] font-bold"
                    style={{ color: styles.softText }}
                  >
                    Private to this crew board.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setComposerOpen(false)}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xl font-black transition hover:scale-105 active:scale-95"
                style={{
                  background: styles.secondaryBg,
                  color: styles.softText,
                }}
                aria-label="Collapse composer"
                title="Collapse"
              >
                ×
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {["🎯 Plan", "📍 Spot", "🎣 Bite note", "🤝 Meetup"].map(
                (label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      const token = label.replace(/^\S+\s*/, "");
                      setText((prev) => (clean(prev) ? prev : `${token}: `));
                    }}
                    className="rounded-full border px-3 py-1.5 text-[11px] font-black transition hover:-translate-y-0.5 active:translate-y-0"
                    style={{
                      background: styles.secondaryBg,
                      borderColor: styles.border,
                      color: styles.softText,
                    }}
                  >
                    {label}
                  </button>
                ),
              )}
            </div>

            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Plan, bite note, spot hint, meetup, or catch recap..."
              rows={4}
              className="w-full resize-none rounded-[12px] border px-4 py-3 text-sm font-semibold leading-6 outline-none transition focus:ring-2 focus:ring-[#b9ebff]/70"
              style={{
                background: styles.inputBg,
                color: styles.pageText,
                borderColor: styles.border,
              }}
              autoFocus
            />

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy || !composerHasText}
                onClick={submitPost}
                className="min-w-0 flex-1 rounded-[10px] border px-4 py-3 text-sm font-black  transition hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background: composerHasText
                    ? styles.primaryButtonBg
                    : styles.secondaryBg,
                  color: composerHasText
                    ? styles.primaryButtonText
                    : styles.softText,
                  borderColor: composerHasText
                    ? styles.primaryBorder
                    : styles.border,
                }}
              >
                {busy ? "Posting…" : "Share to crew"}
              </button>
              {composerHasText ? (
                <button
                  type="button"
                  onClick={() => setText("")}
                  className="rounded-[10px] border px-3 py-3 text-xs font-black transition hover:-translate-y-0.5 active:translate-y-0"
                  style={{
                    background: styles.secondaryBg,
                    borderColor: styles.border,
                    color: styles.softText,
                  }}
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        )}
      </section>

      <section className="space-y-3">
        {displayPosts.length ? (
          displayPosts.map((post) => {
            const mine = post.authorId === uid;
            const commentsOpen = openCommentsFor === post.id;
            const myReaction = postReactions[post.id] || null;
            const likeCount = Math.max(0, Number(post.likeCount || 0));
            const isEditing = editingPostId === post.id;

            return (
              <article
                key={post.id}
                data-crew-post-id={post.id}
                className="relative overflow-hidden rounded-[18px] border"
                style={{
                  background: styles.postBg,
                  borderColor:
                    clean(initialPostId) === post.id
                      ? (isDarkMode ? "rgba(139,124,246,0.64)" : "rgba(109,91,208,0.48)")
                      : styles.border,
                  color: styles.pageText,
                  boxShadow: styles.cardShadow,
                }}
              >
                <div className="p-3.5">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => onOpenProfile?.(post.authorId)}
                      className="h-11 w-11 shrink-0 overflow-hidden rounded-full border"
                      style={{ borderColor: styles.primaryBorder }}
                    >
                      <img
                        src={post.authorAvatar || DEFAULT_AVATAR}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => onOpenProfile?.(post.authorId)}
                            className="block truncate text-left text-sm font-black"
                            style={{ color: styles.pageText }}
                          >
                            {post.authorName || "Angler"}
                          </button>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            <span
                              className="inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.10em]"
                              style={{
                                background: styles.secondaryBg,
                                borderColor: styles.border,
                                color: styles.softText,
                              }}
                            >
                              Private crew
                            </span>
                            <span
                              className="text-[10px] font-bold"
                              style={{ color: styles.softText }}
                            >
                              {fmtTime(post.timestamp)}{editedLabel(post)}
                            </span>
                          </div>
                        </div>
                        <CrewPostMenu
                          styles={styles}
                          onShare={async () => {
                            const result = await share({
                              url: buildCrewPostShareUrl(crewId, post.id),
                            });
                            if (result.ok && result.method === "clipboard") {
                              addToast("Private crew post link copied.", "success");
                            } else if (!result.ok) {
                              addToast("Could not share that crew post.", "error");
                            }
                          }}
                          onEdit={mine ? () => startEdit(post) : undefined}
                          onRemove={mine || canModerate ? () => hidePost(post.id) : undefined}
                          disabled={savingEdit}
                        />
                      </div>

                      {isEditing ? (
                        <div
                          className="mt-3 rounded-[12px] border p-2"
                          style={{
                            background: styles.commentBg,
                            borderColor: styles.border,
                          }}
                        >
                          <textarea
                            value={editingText}
                            onChange={(event) =>
                              setEditingText(event.target.value)
                            }
                            rows={3}
                            className="w-full resize-none rounded-[10px] border px-3 py-2 text-sm font-semibold leading-5 outline-none"
                            style={{
                              background: styles.inputBg,
                              color: styles.pageText,
                              borderColor: styles.border,
                            }}
                          />
                          <div className="mt-2 flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="rounded-[10px] border px-3 py-2 text-xs font-black"
                              style={{
                                background: styles.secondaryBg,
                                borderColor: styles.border,
                                color: styles.pageText,
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={savingEdit || !clean(editingText)}
                              onClick={saveEdit}
                              className="rounded-[10px] border px-3 py-2 text-xs font-black disabled:opacity-50"
                              style={{
                                background: styles.primaryButtonBg,
                                borderColor: styles.primaryBorder,
                                color: styles.primaryButtonText,
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p
                          className="mt-2 whitespace-pre-wrap break-words text-[15px] font-semibold leading-6"
                          style={{ color: styles.pageText }}
                        >
                          {post.text}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <CrewReactionButton
                        styles={styles}
                        count={likeCount}
                        reaction={myReaction}
                        disabled={pendingLikeIds.has(post.id)}
                        onReact={(reaction) => reactToPost(post.id, reaction)}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setOpenCommentsFor(commentsOpen ? null : post.id)
                        }
                        className="inline-flex min-w-[48px] items-center justify-center gap-1 rounded-[10px] border px-3 py-2 text-xs font-black transition hover:-translate-y-0.5"
                        style={{
                          background: styles.secondaryBg,
                          borderColor: styles.border,
                          color: styles.pageText,
                        }}
                        aria-expanded={commentsOpen}
                      >
                        <span aria-hidden="true">💬</span>
                        {post.commentCount ? (
                          <span>{post.commentCount}</span>
                        ) : null}
                      </button>

                    </div>

                  </div>

                  {commentsOpen ? (
                    <CrewComments
                      crewId={crewId}
                      postId={post.id}
                      currentUser={currentUser}
                      addToast={addToast}
                      styles={styles}
                      onOpenProfile={onOpenProfile}
                    />
                  ) : null}
                </div>
              </article>
            );
          })
        ) : (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="w-full rounded-[16px] border border-dashed p-5 text-left text-sm font-semibold transition hover:-translate-y-0.5"
            style={{
              background: styles.postBg,
              borderColor: styles.border,
              color: styles.softText,
            }}
          >
            <span
              className="block text-base font-black"
              style={{ color: styles.pageText }}
            >
              Start the private crew thread
            </span>
            <span className="mt-1 block">
              No posts yet. Share a trip plan, bite report, or meetup note.
            </span>
          </button>
        )}
      </section>
    </div>
  );
};

export default CrewBoardView;
